import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * EdgeContext
 *
 * Edge mode = the same React app served on a dual-screen Linux till talking to
 * a LOCAL Spring agent on the same origin. Enabled by building/serving with
 * VITE_EDGE_MODE=true. When enabled the provider:
 *
 *  1. Fetches a per-boot session token from GET /edge/session (retry with
 *     exponential backoff until it succeeds).
 *  2. Polls GET /edge/statusz every 10s and exposes the latest snapshot.
 *  3. POSTs a render-heartbeat to /edge/heartbeat every 5s with the current
 *     screen ('customer' on /customer-display, 'staff' everywhere else) and
 *     the X-Edge-Token header.
 *
 * When edge mode is off the provider is completely inert — no fetches.
 */

export interface EdgeStatus {
  cloud: { reachable: boolean; lastContactAt: string | null };
  sync: { pending: number; acked24h: number; mustReconcile: number };
  snapshot: { version: string | null; ageSeconds: number | null };
  numbering: {
    billRemaining: number;
    kotRemaining: number;
    mode: 'PREALLOCATED' | 'EXHAUSTED';
  };
  heartbeat: { staff: string | null; customer: string | null };
  printer: { status: 'UNKNOWN' | 'OK' | 'ERROR' };
  /** Cash-drawer shift state. Optional so older agents without it still parse. */
  shift?: { open: boolean; openedAt: string | null };
}

export interface EdgeContextType {
  /** True when the build is running in edge (local till) mode. */
  isEdgeMode: boolean;
  /** Latest /edge/statusz payload, or null before first poll / when unreachable. */
  status: EdgeStatus | null;
  /** True once a statusz poll has failed — the local agent is not responding. */
  agentUnreachable: boolean;
  /** Current per-boot session token, or null before /edge/session succeeds. */
  getToken: () => string | null;
}

const STATUS_POLL_MS = 10_000;
const HEARTBEAT_MS = 5_000;
const SESSION_RETRY_BASE_MS = 1_000;
const SESSION_RETRY_MAX_MS = 30_000;

const INERT_VALUE: EdgeContextType = {
  isEdgeMode: false,
  status: null,
  agentUnreachable: false,
  getToken: () => null,
};

const EdgeContext = createContext<EdgeContextType>(INERT_VALUE);

// eslint-disable-next-line react-refresh/only-export-components
export const useEdge = (): EdgeContextType => useContext(EdgeContext);

interface EdgeProviderProps {
  children: ReactNode;
}

export const EdgeProvider: React.FC<EdgeProviderProps> = ({ children }) => {
  const isEdgeMode = import.meta.env.VITE_EDGE_MODE === 'true';
  const [status, setStatus] = useState<EdgeStatus | null>(null);
  const [agentUnreachable, setAgentUnreachable] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const location = useLocation();

  // Which physical screen this render tree is driving. Kept in a ref so the
  // heartbeat interval always reads the current value without restarting.
  const screenRef = useRef<'staff' | 'customer'>('staff');
  screenRef.current = location.pathname === '/customer-display' ? 'customer' : 'staff';

  // 1. Per-boot session token — retry with exponential backoff until success.
  useEffect(() => {
    if (!isEdgeMode) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const fetchSession = async () => {
      try {
        const res = await fetch('/edge/session');
        if (!res.ok) throw new Error(`session ${res.status}`);
        const data = await res.json();
        if (!cancelled) tokenRef.current = data.token ?? null;
      } catch {
        if (cancelled) return;
        attempt += 1;
        const delay = Math.min(SESSION_RETRY_BASE_MS * 2 ** attempt, SESSION_RETRY_MAX_MS);
        timer = setTimeout(fetchSession, delay);
      }
    };

    fetchSession();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isEdgeMode]);

  // 2. Status poll every 10s. Failure => status null + agentUnreachable.
  useEffect(() => {
    if (!isEdgeMode) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch('/edge/statusz');
        if (!res.ok) throw new Error(`statusz ${res.status}`);
        const data: EdgeStatus = await res.json();
        if (!cancelled) {
          setStatus(data);
          setAgentUnreachable(false);
        }
      } catch {
        if (!cancelled) {
          setStatus(null);
          setAgentUnreachable(true);
        }
      }
    };

    poll();
    const id = setInterval(poll, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isEdgeMode]);

  // 3. Render-heartbeat every 5s (best-effort; requires session token).
  useEffect(() => {
    if (!isEdgeMode) return;

    const beat = () => {
      const token = tokenRef.current;
      if (!token) return; // session not established yet
      fetch('/edge/heartbeat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Edge-Token': token,
        },
        body: JSON.stringify({ screen: screenRef.current }),
      }).catch(() => {
        // Best-effort: the statusz poll is the authoritative degraded signal.
      });
    };

    const id = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [isEdgeMode]);

  const getToken = useCallback(() => tokenRef.current, []);

  const value = useMemo<EdgeContextType>(
    () => (isEdgeMode ? { isEdgeMode, status, agentUnreachable, getToken } : INERT_VALUE),
    [isEdgeMode, status, agentUnreachable, getToken],
  );

  return <EdgeContext.Provider value={value}>{children}</EdgeContext.Provider>;
};

export default EdgeProvider;
